import { AccountAttachmentRequirementStatus } from "models/Account";
import React, { useState } from "react";
import { Alert, Badge, Button, Input, Spinner } from "reactstrap";
import { uploadAccountAttachment } from "services/AccountApi";
import AccountAttachmentFormWizard from "./AccountAttachmentFormWizard";
import { useAccountTranslation } from "./translations";

interface Props {
    accountId: string;
    compliant: boolean;
    requirements: AccountAttachmentRequirementStatus[];
    onReload: () => Promise<void>;
}

interface UploadDraft {
    file?: { name: string; content: string };
    expiresAt?: string;
}

const attachmentStatusKeys: Record<string, string> = {
    missing: "attachmentStatusMissing",
    missing_expiry: "attachmentStatusMissingExpiry",
    expired: "attachmentStatusExpired",
    rejected: "attachmentStatusRejected",
    pending_approval: "attachmentStatusPendingApproval",
    approved: "attachmentStatusApproved",
};

const badgeColor = (status: string) => {
    if (status === "approved") return "success";
    if (status === "pending_approval") return "warning";
    return "danger";
};

const AccountAttachmentPanel: React.FC<Props> = ({ accountId, compliant, requirements, onReload }) => {
    const { locale, t } = useAccountTranslation();
    const [drafts, setDrafts] = useState<Record<string, UploadDraft>>({});
    const [busy, setBusy] = useState("");
    const [message, setMessage] = useState<{ color: string; text: string } | null>(null);
    const relevant = requirements;

    if (relevant.length === 0) return null;

    const selectFile = (requirementId: string, file?: File) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result !== "string") return;
            setDrafts(values => ({
                ...values,
                [requirementId]: { ...values[requirementId], file: { name: file.name, content: reader.result as string } },
            }));
        };
        reader.readAsDataURL(file);
    };

    const upload = async (requirement: AccountAttachmentRequirementStatus) => {
        const draft = drafts[requirement.requirement.id];
        if (!draft?.file) return;
        setBusy(requirement.requirement.id);
        setMessage(null);
        try {
            await uploadAccountAttachment(
                accountId,
                requirement.requirement.id,
                draft.file,
                draft.expiresAt
            );
            setDrafts(values => ({ ...values, [requirement.requirement.id]: {} }));
            setMessage({ color: "success", text: t("attachmentUploadSuccess") });
            await onReload();
        } catch {
            setMessage({ color: "danger", text: t("attachmentUploadError") });
        } finally {
            setBusy("");
        }
    };

    const formSubmitted = async () => {
        setMessage({ color: "success", text: t("attachmentFormSubmitSuccess") });
        await onReload();
    };

    return <section className="portal-settings-section">
        <div className="portal-settings-heading">
            <span><i className="mdi mdi-file-certificate-outline" aria-hidden="true" /></span>
            <div>
                <h2>{t("requiredDocumentsTitle")}</h2>
                <p>{t("requiredDocumentsBlocked")}</p>
            </div>
        </div>
        <div className="portal-settings-content">
            {!compliant && <Alert color="warning">{t("requiredDocumentsBlocked")}</Alert>}
            {message && <Alert color={message.color}>{message.text}</Alert>}
            {relevant.map(requirement => {
                const draft = drafts[requirement.requirement.id] ?? {};
                const canUpload = !!draft.file && (!requirement.requirement.requires_expiry || !!draft.expiresAt);
                const canProvide = requirement.status !== "approved" && requirement.status !== "pending_approval";
                const isForm = requirement.requirement.kind === "form";
                return <div key={requirement.requirement.id} className="border-top py-3">
                    <div className="d-flex justify-content-between gap-2 mb-2">
                        <div>
                            <strong>{requirement.requirement.name}</strong>
                            {requirement.requirement.description && <div className="small text-muted">{requirement.requirement.description}</div>}
                            {requirement.attachment?.expires_at && <div className="small text-muted">
                                {t("attachmentExpires")}: {new Date(requirement.attachment.expires_at).toLocaleDateString(locale)}
                            </div>}
                            {requirement.status === "rejected" && requirement.attachment?.review_note && <div className="mt-2 text-danger">
                                <strong className="d-block small">{t("attachmentRejectionReason")}</strong>
                                <span>{requirement.attachment.review_note}</span>
                            </div>}
                        </div>
                        <Badge color={badgeColor(requirement.status)} className="align-self-start">
                            {attachmentStatusKeys[requirement.status]
                                ? t(attachmentStatusKeys[requirement.status])
                                : requirement.status}
                        </Badge>
                    </div>
                    {canProvide && isForm && <div className="mt-3">
                        <AccountAttachmentFormWizard
                            accountId={accountId}
                            requirement={requirement}
                            onSubmitted={formSubmitted}
                        />
                    </div>}
                    {canProvide && !isForm && <div className="row g-2 align-items-end">
                        <div className={requirement.requirement.requires_expiry ? "col-md-6" : "col-md-9"}>
                            <Input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                                onChange={event => selectFile(requirement.requirement.id, event.target.files?.[0])}
                            />
                        </div>
                        {requirement.requirement.requires_expiry && <div className="col-md-3">
                            <label className="form-label small mb-1">{t("attachmentExpires")}</label>
                            <Input
                                type="date"
                                min={new Date().toISOString().slice(0, 10)}
                                value={draft.expiresAt ?? ""}
                                onChange={event => setDrafts(values => ({
                                    ...values,
                                    [requirement.requirement.id]: { ...values[requirement.requirement.id], expiresAt: event.target.value },
                                }))}
                            />
                        </div>}
                        <div className="col-md-3">
                            <Button color="primary" className="w-100" disabled={!canUpload || busy === requirement.requirement.id}
                                onClick={() => upload(requirement)}>
                                {busy === requirement.requirement.id ? <Spinner size="sm" /> : t("attachmentUpload")}
                            </Button>
                        </div>
                    </div>}
                </div>;
            })}
        </div>
    </section>;
};

export default AccountAttachmentPanel;
